import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useRef, type ReactNode } from "react";

import { cn } from "@/lib/cn";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

/**
 * Radix owns focus trapping, escape handling, and `aria-modal`. The title and
 * description are required rather than optional so every dialog is announced
 * with a name and a purpose.
 */
export function DialogContent({
  title,
  description,
  children,
  footer,
  className,
  busy = false,
}: {
  title: string;
  description: string;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  /**
   * A non-idempotent request is in flight and dismissing the dialog would
   * neither cancel it nor tell the caller what happened. The close control is
   * removed rather than disabled: there is no reason to attach that is more
   * useful than its absence, and ADR 0001 accepts "not rendered" as the other
   * half of the no-dead-controls rule. Escape and the overlay are refused by
   * the owner's `onOpenChange`, so this keeps the header honest about it.
   */
  busy?: boolean;
}) {
  /**
   * The control that had focus when this dialog opened.
   *
   * A modal Radix dialog restores focus to its own `DialogTrigger`, and every
   * dialog in this app is opened from application state — a catalog row's
   * lifecycle action, a toolbar button — rather than from a trigger inside the
   * `Dialog` tree. With no trigger to return to, Radix's close handler
   * suppresses the focus-scope's own restore and then focuses nothing, so
   * dismissal drops focus onto `<body>` and a keyboard user loses their place
   * in the list they were working through. Capturing the opener as the dialog
   * mounts — `onOpenAutoFocus` fires while it still holds focus — lets the
   * close hand it back.
   */
  const openerRef = useRef<HTMLElement | null>(null);

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/40" />
      <DialogPrimitive.Content
        onOpenAutoFocus={() => {
          const opener = document.activeElement;
          openerRef.current =
            opener instanceof HTMLElement && opener !== document.body
              ? opener
              : null;
          // Not prevented: Radix still moves focus into the dialog.
        }}
        onCloseAutoFocus={(event) => {
          const opener = openerRef.current;
          openerRef.current = null;
          // A control the close itself removed — the archived row that just
          // left the catalog — cannot be focused. Defer to Radix rather than
          // preventing a restore this cannot perform.
          if (!opener?.isConnected) {
            return;
          }
          event.preventDefault();
          opener.focus();
        }}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2",
          "flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-md border border-border bg-card shadow-lg",
          className,
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="space-y-1">
            <DialogPrimitive.Title className="text-base font-semibold">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-muted-foreground">
              {description}
            </DialogPrimitive.Description>
          </div>
          {busy ? null : (
            <DialogPrimitive.Close
              aria-label="Close dialog"
              className="rounded-md p-1 text-muted-foreground hover:bg-secondary"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </DialogPrimitive.Close>
          )}
        </div>
        {children ? (
          <div
            data-slot="dialog-scroll-area"
            className="min-h-0 overflow-y-auto px-5 py-4"
          >
            {children}
          </div>
        ) : null}
        {footer ? (
          <div className="flex shrink-0 justify-end gap-2 border-t border-border px-5 py-3">
            {footer}
          </div>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
